import { path } from 'ramda'
import { combineReducers } from 'redux'
import { createAction as actionCreator } from 'redux-actions'

import config from 'config'

import createReducer from './createReducer'

const { REDUX_ACTION_NAMESPACE } = config

const actionType = (action) => `${REDUX_ACTION_NAMESPACE}_${action}`

const createAction = (type) => actionCreator(actionType(type))

const receieveActionType = (stateName: string) =>
  actionType(`RECEIVE_${stateName.toUpperCase()}`)

interface ReceiveOptions {
  merge?: boolean
}

const createReceiveAction = <Payload>(stateName: string) =>
  actionCreator<Payload, Payload>(
    receieveActionType(stateName),
    (payload: Payload) => payload
  )

const createReceiveActionWithOptions = <Payload>(stateName: string) =>
  actionCreator<Payload, { options?: ReceiveOptions }, Payload, ReceiveOptions>(
    receieveActionType(stateName),
    (payload: Payload) => payload,
    (_, options?) => (options ? { options: options as ReceiveOptions } : {})
  )

const createReceiveReducer = (initialState: any, stateName: string) =>
  createReducer(initialState, {
    [receieveActionType(stateName)]: ({ draft, action: { payload, meta } }) => {
      if (path(['options', 'merge'], meta)) {
        if (Array.isArray(draft)) {
          return [...draft, ...payload]
        }
        return {
          ...draft,
          ...payload,
        }
      }
      return payload
    },
  })

const mergeReducers = (reducers) => [
  {
    key: REDUX_ACTION_NAMESPACE,
    reducer: combineReducers(reducers),
  },
]

const selectState = <T = any>(state, name: string) =>
  path<T>([REDUX_ACTION_NAMESPACE, name], state)
const selectGlobalState = <T = any>(state, name: string) =>
  path<T>([name], state)

const rootSelector =
  <T = any>(name: string) =>
  (state): T =>
    selectState<T>(state, name)!
const globalSelector =
  <T = any>(name: string) =>
  (state): T =>
    selectGlobalState<T>(state, name)!

export {
  actionType,
  combineReducers,
  createReducer,
  createReceiveAction,
  createReceiveActionWithOptions,
  createReceiveReducer,
  createAction,
  mergeReducers,
  REDUX_ACTION_NAMESPACE,
  rootSelector,
  globalSelector,
  selectState,
}
